import { supabase } from "@/integrations/supabase/client";

type MPEventType = "board_post" | "interaction_received" | "player_joined" | "player_left" | "player_kicked";

interface MPNotificationOpts {
  adventureId: string;
  recipientUserIds: string[];
  type: MPEventType;
  title: string;
  body?: string;
}

/**
 * Create multiplayer notifications for a set of players.
 * Inserts into the existing `notifications` table.
 */
export async function createMPNotification({ adventureId, recipientUserIds, type, title, body }: MPNotificationOpts) {
  if (recipientUserIds.length === 0) return;

  // Use SECURITY DEFINER RPC — RLS only allows users to insert notifications
  // for themselves; this RPC verifies sender shares an adventure with the target.
  await Promise.all(
    recipientUserIds.map((userId) =>
      supabase.rpc("send_notification" as any, {
        _target_user_id: userId,
        _type: `mp_${type}`,
        _title: title,
        _body: body || null,
        _link: `/play/${adventureId}`,
      }).then(({ error }) => {
        if (error) console.error("[MP_NOTIFICATION] send_notification failed:", error);
      })
    )
  );
}

/**
 * Notify other players about a new board thread/post.
 */
export function notifyBoardActivity(adventureId: string, authorUserId: string, allPlayerUserIds: string[], threadTitle: string) {
  const recipients = allPlayerUserIds.filter((id) => id !== authorUserId);
  return createMPNotification({
    adventureId,
    recipientUserIds: recipients,
    type: "board_post",
    title: `New post in "${threadTitle}"`,
    body: "Check the adventure board for updates.",
  });
}

/**
 * Notify a player about an incoming interaction (trade, gift, challenge).
 */
export function notifyInteraction(adventureId: string, targetUserId: string, fromName: string, interactionType: string) {
  return createMPNotification({
    adventureId,
    recipientUserIds: [targetUserId],
    type: "interaction_received",
    title: `${fromName} sent you a ${interactionType.replace("_", " ")}`,
  });
}

/**
 * Notify other players about someone leaving/joining.
 */
export function notifyPlayerChange(adventureId: string, allPlayerUserIds: string[], excludeUserId: string, playerName: string, action: "joined" | "left" | "kicked") {
  const recipients = allPlayerUserIds.filter((id) => id !== excludeUserId);
  return createMPNotification({
    adventureId,
    recipientUserIds: recipients,
    type: action === "kicked" ? "player_kicked" : action === "joined" ? "player_joined" : "player_left",
    title: `${playerName} ${action} the adventure`,
  });
}
